import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Row,
    Col,
    Select,
    notification,
    Empty,
    SelectProps,
    Checkbox,
    Form,
    FormInstance,
} from 'antd';

import AplicativoSvc, { IObterAplicativos } from '../../services/AplicativoSvc';
import TagSituacao from '../tag/TagSituacao';

const { Option } = Select;

interface ISearchAplicativoProps extends SelectProps {
    formItemName?: string;
    form?: FormInstance;
    filtroExcluidos?: boolean;
    value: IObterAplicativos | null;
    onChange: (val: IObterAplicativos | null) => void;
}

function salvarUltimoSelecionado(app?: IObterAplicativos | null) {
    if (app) {
        localStorage.setItem('@ultimoAppSelecionado', JSON.stringify(app));
    }
}

export function obterUltimoAplicativoSelecionado(): IObterAplicativos | null {
    const app = localStorage.getItem('@ultimoAppSelecionado');
    if (app) {
        return JSON.parse(app);
    }
    return null;
}

function SearchAplicativo({
    filtroExcluidos,
    formItemName,
    value,
    onChange,
    form,
    ...props
}: ISearchAplicativoProps) {
    const timeSearch = useRef<any>(null);
    const [loading, setLoading] = useState(false);
    const [aplicativos, setAplicativos] = useState<IObterAplicativos[]>([]);
    const [textoPesquisa, setTextoPesquisa] = useState('');
    const [excluidos, setExcluidos] = useState(false);

    const carregarListaAplicativos = useCallback(async () => {
        try {
            setLoading(true);
            const response = await AplicativoSvc.obterAplicativos({
                page: 1,
                limit: 10,
                excluido: excluidos,
                textoPesquisa,
            });

            setAplicativos(response.result);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            notification.error({ message: 'Oops!', description: error.message });
        }
    }, [excluidos, textoPesquisa]);

    function onSelect() {
        clearTimeout(timeSearch.current);
        setTextoPesquisa('');
    }

    function onChangeSelected(selectedId: string) {
        const selected = aplicativos.find(({ id }) => id === selectedId) ?? null;
        onChange(selected);
        salvarUltimoSelecionado(selected);
    }

    useEffect(() => {
        clearTimeout(timeSearch.current);
        timeSearch.current = setTimeout(() => {
            carregarListaAplicativos();
        }, 600);
    }, [carregarListaAplicativos, textoPesquisa]);

    useEffect(() => {
        if (value) {
            setAplicativos((val) => {
                const existe = val.some(({ id }) => id === value.id);
                if (!existe) {
                    const cloneArray = [...val];
                    cloneArray.push(value);
                    return cloneArray;
                }
                return val;
            });
            if (form && formItemName) {
                form?.setFieldsValue({
                    [formItemName as string]: value.id,
                });
            }
        }
    }, [form, formItemName, value]);

    return (
        <Form.Item
            id={formItemName}
            name={formItemName}
            noStyle
            rules={[
                {
                    required: true,
                    message: 'Informe o aplicativo!',
                },
            ]}
        >
            <Select
                showSearch
                placeholder="Digite o nome do app..."
                defaultActiveFirstOption={false}
                showArrow={true}
                filterOption={false}
                loading={loading}
                searchValue={textoPesquisa}
                onSearch={(val) => setTextoPesquisa(val)}
                onSelect={() => onSelect()}
                notFoundContent={
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nada encontrado" />
                }
                style={{ width: '100%' }}
                dropdownRender={(menu) => (
                    <Col>
                        <Row justify="end">
                            {filtroExcluidos && (
                                <Checkbox
                                    checked={excluidos}
                                    onChange={(e) => setExcluidos(e.target.checked)}
                                    style={{ paddingBottom: 10 }}
                                >
                                    Excluídos?
                                </Checkbox>
                            )}
                        </Row>

                        {menu}
                    </Col>
                )}
                {...props}
                value={value?.id}
                onChange={(val) => onChangeSelected(val)}
            >
                {aplicativos.map(({ id, nome, excluidoEm }) => (
                    <Option key={id} value={id}>
                        <Row align="middle">
                            <Col span={12}>{nome}</Col>
                            <Col span={12}>
                                <Row justify="end">
                                    <TagSituacao ativo={!excluidoEm} />
                                </Row>
                            </Col>
                        </Row>
                    </Option>
                ))}
            </Select>
        </Form.Item>
    );
}

export default SearchAplicativo;
